import { useState } from 'react';
import {
  CheckCircle2,
  XCircle,
  Radio,
  Power,
  RefreshCw,
  Server,
  ShieldAlert,
} from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { SlideOver } from '@/components/ui/SlideOver';
import {
  usePublishProperty,
  useUnpublishProperty,
  useSyncToPms,
} from '@/hooks/api/usePlatform';
import { extractErrorMessage } from '@/lib/error';
import { showToast } from '@/lib/toast';
import { cn } from '@/lib/utils';
import type {
  PcPropertyCore,
  PcPublishGate,
  PcPms,
  PcSyncResult,
} from '@/services/api/property-content.api';
import { fieldInputClass } from './fields';

const REASON_LABELS: Record<string, string> = {
  inactive: 'Property is inactive',
  not_approved: 'Property is not approved',
  no_priced_room: 'No active priced room',
};

interface GateRow {
  label: string;
  ok: boolean;
}

function buildGateRows(gate: PcPublishGate): GateRow[] {
  return [
    { label: 'Property is active', ok: !gate.reasons.includes('inactive') },
    { label: 'Property is approved', ok: !gate.reasons.includes('not_approved') },
    { label: 'At least one active priced room', ok: !gate.reasons.includes('no_priced_room') },
  ];
}

export function PublishTab({
  property,
  publishGate,
  pms,
}: {
  property: PcPropertyCore;
  publishGate: PcPublishGate;
  pms: PcPms;
}) {
  const publishMutation = usePublishProperty(property.id);
  const unpublishMutation = useUnpublishProperty(property.id);
  const syncMutation = useSyncToPms(property.id);

  const [syncOpen, setSyncOpen] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [syncResult, setSyncResult] = useState<PcSyncResult | null>(null);
  const [syncError, setSyncError] = useState('');

  const rows = buildGateRows(publishGate);
  const firstReason = publishGate.reasons[0];

  async function handlePublish() {
    try {
      await publishMutation.mutateAsync();
      showToast('Property activated · live on storefront');
    } catch (err) {
      showToast(extractErrorMessage(err), 'error');
    }
  }
  async function handleUnpublish() {
    try {
      await unpublishMutation.mutateAsync();
      showToast('Property taken offline');
    } catch (err) {
      showToast(extractErrorMessage(err), 'error');
    }
  }

  function openSync() {
    setConfirmText('');
    setSyncError('');
    setSyncResult(null);
    setSyncOpen(true);
  }

  async function handleSync() {
    if (confirmText !== 'CONFIRM') return;
    setSyncError('');
    try {
      const result = await syncMutation.mutateAsync('CONFIRM');
      setSyncResult(result);
      setSyncOpen(false);
      showToast('Synced to PMS backend');
    } catch (err) {
      setSyncError(extractErrorMessage(err));
    }
  }

  return (
    <div className="space-y-6">
      {/* ── Publish gate panel ──────────────────────────────────────────── */}
      <section className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm" aria-live="polite">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-base font-semibold text-gray-900">Storefront status</h2>
          <span
            className={cn(
              'inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-sm font-semibold',
              publishGate.isLive ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-800',
            )}
          >
            <Radio size={14} aria-hidden="true" />
            {publishGate.isLive ? 'Live' : 'Draft'}
          </span>
        </div>

        <p className="mb-4 text-sm text-gray-500">
          {publishGate.isLive
            ? 'This listing is live and visible to guests on the storefront.'
            : firstReason
              ? `Why it's a draft: ${REASON_LABELS[firstReason] ?? firstReason}.`
              : 'Resolve the checklist below to go live.'}
        </p>

        <ul className="space-y-2">
          {rows.map((row) => (
            <li key={row.label} className="flex items-center gap-2 text-sm">
              {row.ok ? (
                <CheckCircle2 size={18} className="text-green-600 shrink-0" aria-hidden="true" />
              ) : (
                <XCircle size={18} className="text-gray-300 shrink-0" aria-hidden="true" />
              )}
              <span className={row.ok ? 'text-gray-700' : 'text-gray-400'}>{row.label}</span>
            </li>
          ))}
        </ul>

        <p className="mt-4 text-sm text-gray-500">
          Priced rooms: <span className="font-semibold text-gray-700">{publishGate.pricedRoomCount}</span>
        </p>

        <div className="mt-5 flex flex-wrap items-center gap-3 border-t border-gray-100 pt-4">
          {property.isActive ? (
            <Button variant="secondary" onClick={handleUnpublish} isLoading={unpublishMutation.isPending} disabled={unpublishMutation.isPending}>
              <Power size={16} aria-hidden="true" />
              Unpublish (set inactive)
            </Button>
          ) : (
            <Button onClick={handlePublish} isLoading={publishMutation.isPending} disabled={publishMutation.isPending}>
              <Power size={16} aria-hidden="true" />
              Publish (set active)
            </Button>
          )}
          {!property.isActive && (
            <span className="text-xs text-gray-400">
              Activating alone won't make it live unless the other checks pass.
            </span>
          )}
        </div>
      </section>

      {/* ── Sync to PMS (TASK-112, D11 double guard) ────────────────────── */}
      <section className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
        <div className="mb-2 flex items-center gap-2">
          <Server size={18} className="text-gray-500" aria-hidden="true" />
          <h2 className="text-base font-semibold text-gray-900">Sync to PMS backend</h2>
        </div>
        <p className="mb-4 text-sm text-gray-500">
          Project this listing's content (amenities + operational defaults) into the operational
          PMS read model. This is a destructive overwrite and requires explicit confirmation.
        </p>

        {pms.pmsSyncedAt && (
          <p className="mb-4 text-sm text-gray-600">
            Last synced: <span className="font-medium text-gray-800">{new Date(pms.pmsSyncedAt).toLocaleString()}</span>
          </p>
        )}

        {syncResult && (
          <div className="mb-4 rounded-lg border border-green-200 bg-green-50 p-3 text-sm text-green-800" role="status">
            Synced {syncResult.amenitiesSynced} amenities across {syncResult.unitsCount} rooms at{' '}
            {new Date(syncResult.syncedAt).toLocaleString()}.
          </div>
        )}

        {!pms.hasPmsIntegration ? (
          <div className="flex items-start gap-2 rounded-lg border border-gray-200 bg-gray-50 p-3">
            <ShieldAlert size={18} className="text-gray-400 shrink-0 mt-0.5" aria-hidden="true" />
            <div>
              <Button variant="secondary" disabled>
                <RefreshCw size={16} aria-hidden="true" />
                Sync to PMS backend
              </Button>
              <p className="mt-2 text-sm text-gray-500">This hotel does not have PMS integration.</p>
            </div>
          </div>
        ) : (
          <Button onClick={openSync}>
            <RefreshCw size={16} aria-hidden="true" />
            Sync to PMS backend
          </Button>
        )}
      </section>

      <SlideOver isOpen={syncOpen} onClose={() => !syncMutation.isPending && setSyncOpen(false)} title="Confirm PMS sync">
        <div className="space-y-4">
          <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3">
            <ShieldAlert size={18} className="text-amber-600 shrink-0 mt-0.5" aria-hidden="true" />
            <p className="text-sm text-amber-800">
              This overwrites the operational PMS read model for <strong>{property.name}</strong> with
              the current listing content. Type <strong>CONFIRM</strong> to proceed.
            </p>
          </div>

          {syncError && (
            <div className="rounded-lg bg-red-50 border border-red-200 p-3 text-sm text-red-700" role="alert">
              {syncError}
            </div>
          )}

          <div>
            <label htmlFor="sync-confirm" className="block text-sm font-medium text-gray-700 mb-1">
              Type CONFIRM
            </label>
            <input
              id="sync-confirm"
              type="text"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              autoComplete="off"
              className={fieldInputClass}
              placeholder="CONFIRM"
            />
          </div>

          <div className="flex items-center justify-end gap-3 border-t border-gray-100 pt-3">
            <Button variant="secondary" onClick={() => setSyncOpen(false)} disabled={syncMutation.isPending}>
              Cancel
            </Button>
            <Button
              onClick={handleSync}
              isLoading={syncMutation.isPending}
              disabled={confirmText !== 'CONFIRM' || syncMutation.isPending}
            >
              <RefreshCw size={16} aria-hidden="true" />
              Sync now
            </Button>
          </div>
        </div>
      </SlideOver>
    </div>
  );
}
